import { type MouseEventHandler, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
} from '@mui/material';
import { Image } from '@realadvisor/image';
import { MoreVertical } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type { LeadCardFragment } from '@graphql/__generated__/graphql';
import { ConditionalLinkWrapper } from '@modules/_shared/components/ConditionalLinkWrapper';
import { SalesLeadDetail } from '@modules/_shared/components/lead/SalesLeadDetail';
import { LeadSelect } from '@modules/_shared/components/LeadsSelect';
import { MutationErrorModal } from '@modules/_shared/components/MutationErrorModal';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { userCanViewLeadDetails } from '@modules/_shared/utils/listingPermissions';
import { useLocale } from '@shared/hooks/locale';
import {
  getCurrencyByCountryCode,
  getDefaultLocationOnMap,
} from '@shared/LocaleContext';
import { useIsDarkMode } from '@shared/theme/useIsDarkMode';
import { getStaticMapLink } from '@shared/utils/static-map';

export const LEAD_CARD_FRAGMENT = gql(/* GraphQL */ `
  fragment LeadCard on leads {
    id
    ...LeadQualificationFunnel
    ...LeadUpdatedTimeAgo
    ...SalesLeadDetail
    created_by
    origin_enquiry_id
    property {
      id
      lat
      lng
      country_code
    }
    lead_agents {
      id
      user_id
      source
      claimed_at
    }
  }
`);

export enum ELeadCardDisplayMode {
  NORMAL,
  COMPACT,
}

interface LeadCardProps {
  lead: LeadCardFragment;
  showMap?: boolean;
  showAddress?: boolean;
  controls?: React.ReactNode;
  bottomButton?: React.ReactNode;
  openOnNewTab?: boolean;
  hasLink?: boolean;
  customCreationDate?: string;
  displayMode?: ELeadCardDisplayMode;
  onLeadChanged?: (
    leadId: string | null,
    onError: (err: ErrorLike) => void,
  ) => Promise<any>;
}

export const LeadCard: React.FC<React.PropsWithChildren<LeadCardProps>> = ({
  lead,
  hasLink = true,
  openOnNewTab = false,
  onLeadChanged,
  children,
  ...restProps
}) => {
  const { t } = useLocale();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [updateError, setUpdateError] = useState<null | ErrorLike>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  if (!lead) {
    return null;
  }

  const isMenuOpened = Boolean(anchorEl);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (onLeadChanged == null) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Menu
        open={isMenuOpened}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        anchorEl={anchorEl}
      >
        <MenuItem
          onClick={() => {
            setIsEditing(true);
            handleCloseMenu();
          }}
        >
          {t('Edit')}
        </MenuItem>
        <MenuItem
          onClick={async () => {
            if (onLeadChanged == null) {
              return;
            }
            setIsDeleting(true);
            handleCloseMenu();
            await onLeadChanged(null, setUpdateError);
            setIsDeleting(false);
          }}
        >
          {t('Remove')}
        </MenuItem>
      </Menu>
      <MutationErrorModal
        error={updateError}
        onClose={() => setUpdateError(null)}
      />

      {isEditing ? (
        <Stack direction="row" spacing={1}>
          <LeadSelect
            placeholder={t('Select a lead')}
            leadId={lead.id}
            sx={{ flexGrow: 1 }}
            autoFocus
            disabled={isUpdating}
            onChange={async lead => {
              if (lead == null || onLeadChanged == null) {
                return;
              }

              setIsUpdating(true);
              await onLeadChanged(lead.id, setUpdateError);
              setIsUpdating(false);
              setIsEditing(false);
            }}
          />
          <Button
            variant="outlined"
            onClick={() => setIsEditing(false)}
            disabled={isUpdating}
            size="small"
          >
            {t('Cancel')}
          </Button>

          <Backdrop
            open={isUpdating}
            sx={{
              zIndex: 1,
              position: 'absolute',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
            }}
          >
            <CircularProgress disableShrink />
          </Backdrop>
        </Stack>
      ) : (
        <ConditionalLinkWrapper
          link={
            hasLink
              ? {
                  to: `/leads/${lead.id}`,
                  target: openOnNewTab ? '_blank' : '_self',
                }
              : undefined
          }
          css={{
            display: 'block',
            height:
              restProps.displayMode === ELeadCardDisplayMode.COMPACT
                ? 'fit-content'
                : undefined,
          }}
        >
          <LeadSnippet
            lead={lead}
            {...restProps}
            isDeleting={isDeleting}
            onMoreActionsClicked={
              onLeadChanged == null ? undefined : handleOpenMenu
            }
          >
            {children}
          </LeadSnippet>
        </ConditionalLinkWrapper>
      )}
    </>
  );
};

const LeadSnippet: React.FC<
  React.PropsWithChildren<
    Omit<
      LeadCardProps,
      'hasLink' | 'openOnNewTab' | 'legacyLinks' | 'onLeadChanged'
    > & {
      isDeleting?: boolean;
      onMoreActionsClicked?: MouseEventHandler<HTMLButtonElement>;
    }
  >
> = ({
  lead,
  showMap = true,
  showAddress = true,
  controls = null,
  bottomButton = null,
  customCreationDate = null,
  displayMode = ELeadCardDisplayMode.NORMAL,
  isDeleting,
  onMoreActionsClicked,
  children,
}) => {
  const { me } = useAppData();
  const { countryCode } = useLocale();
  const isDarkMode = useIsDarkMode();

  const { property } = lead;
  const { lat: defaultLat, lng: defaultLng } =
    getDefaultLocationOnMap(countryCode);
  const lat = property?.lat ?? defaultLat;
  const lng = property?.lng ?? defaultLng;
  const currency = getCurrencyByCountryCode(property?.country_code ?? 'CH');
  const canViewLeadDetails = userCanViewLeadDetails(me, lead);
  const isCompact = displayMode === ELeadCardDisplayMode.COMPACT;
  const compactOverflow = isCompact ? 'hidden' : undefined;

  return (
    <Stack spacing={2}>
      <Card sx={{ overflow: compactOverflow }}>
        <CardContent
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            p: 0,
            height: isCompact ? 90 : 'auto',
            pb: '0 !important',
            overflow: compactOverflow,
          }}
        >
          <Backdrop
            open={isDeleting ?? false}
            sx={{
              zIndex: 1,
              position: 'absolute',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
            }}
          >
            <CircularProgress disableShrink />
          </Backdrop>

          {showMap && (
            <Box
              sx={{
                flexGrow: 0,
                flexShrink: 0,
                height: isCompact ? '100%' : 'auto',
                width: isCompact ? 90 : ['100%', '140px'],
              }}
            >
              <Image
                css={{
                  display: isCompact ? 'flex' : ['flex', 'none'],
                  flexFlow: 'column',
                  flex: '1 1 0',
                }}
                loading="lazy"
                src={getStaticMapLink({
                  lat,
                  lng,
                  zoom: 10,
                  width: 128,
                  height: 128,
                  markers: canViewLeadDetails ? [{ lat, lng }] : [],
                  dark: isDarkMode,
                })}
              />
              {!isCompact && (
                <Image
                  css={{
                    display: ['none', 'flex'],
                    flexFlow: 'column',
                    aspectRatio: ['auto', '1 / 1'],
                    flex: '1 1 0',
                  }}
                  loading="lazy"
                  src={getStaticMapLink({
                    lat,
                    lng,
                    zoom: 10,
                    width: 256,
                    height: 256,
                    style: 'satellite',
                    scale: 2,
                    markers: canViewLeadDetails ? [{ lat, lng }] : [],
                  })}
                />
              )}
            </Box>
          )}
          <SalesLeadDetail
            isCompact={isCompact}
            lead={lead}
            canViewLeadDetails={canViewLeadDetails}
            customCreationDate={customCreationDate}
            currency={currency}
            showAddress={showAddress}
            bottomButton={bottomButton}
            controls={controls}
          />
          {onMoreActionsClicked != null && (
            <Box
              sx={{
                flexShrink: 0,
                ...(isCompact
                  ? {
                      alignSelf: 'stretch',
                      boxSizing: 'border-box',
                      pt: 1,
                      pr: 1,
                    }
                  : {
                      display: 'flex',
                      alignItems: 'flex-start',
                      py: 3,
                      pr: 3,
                    }),
              }}
            >
              <IconButton
                size="small"
                onClick={onMoreActionsClicked}
                sx={
                  isCompact
                    ? { width: 24, height: 24, p: 0, color: 'text.secondary' }
                    : undefined
                }
              >
                <MoreVertical size={16} />
              </IconButton>
            </Box>
          )}
        </CardContent>
      </Card>
      {children}
    </Stack>
  );
};
